import { TSTitleCard } from '@/components/shared/card/Card';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat } from '@/helpers/dateHelper';
import { getRequestStatus } from '@/helpers/requestStatusHelper';
import { useAvailabilityExceptionRequests } from '@/services/availability/useAvailabilityExceptionRequests';
import { Feature } from '@/services/backstage/Feature';
import { useGetLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { strings } from '@/services/translation/strings';
import { useWorkingPatternRequests } from '@/services/workingPatterns/useWorkingPatternRequests';
import { FC, Suspense, useMemo } from 'react';
import { FiBell } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { RequestRow, RequestType } from './RequestsTable.types';
import TeamMemberRequestTable from './TeamMemberRequestTable';

interface TeamMemberRequestsProps {
  selectedDate: Date;
  setSelectedDate: (newDate: Date) => void;
}

export const requestsSectionId = 'requestsSectionId';

export const TeamMemberRequests: FC<TeamMemberRequestsProps> = ({ setSelectedDate, selectedDate }) => {
  const { userId } = useParams<'userId'>();
  const navigate = useNavigate();

  if (!userId) {
    throw new Error('userId is required');
  }

  const { data } = useWorkingPatternRequests(userId);

  const { requests: availabilityExceptionRequests } = useAvailabilityExceptionRequests(userId);
  const getLeaveType = useGetLeaveType();
  const today = dateToApiFormat(new Date());

  const requests = useMemo<RequestRow[]>(() => {
    const workingPatternRequests: RequestRow[] = (data?.workingPatternRequestsByEmployeeId ?? []).flatMap(request => {
      if (!request) return [];
      const status = getRequestStatus(request.state);
      return [
        {
          id: request.id,
          dateSubmitted: request.requestedOn,
          type: RequestType.ScheduleChange,
          details: '',
          status,
          isPast: !!request.effectiveFrom && request.effectiveFrom < today
        }
      ];
    });
    const availabilityRequests: RequestRow[] = (availabilityExceptionRequests ?? []).flatMap(request => {
      if (!request) return [];
      const state = request.requestState;
      if (!state) return [];
      const status = getRequestStatus(state);

      const exception = request.exceptions?.[0];
      const leaveType = exception?.leaveType ? getLeaveType(exception.leaveType.id) : undefined;

      return [
        {
          id: request.id,
          dateSubmitted: request.requestedOn,
          type: exception?.leaveType ? RequestType.LeaveRequest : RequestType.AvailabilityChange,
          details: leaveType?.properties.displayName ?? '',
          status,
          isPast: request.end < today
        }
      ];
    });

    return [...workingPatternRequests, ...availabilityRequests];
  }, [data, availabilityExceptionRequests, today]);

  const handleViewDetails = (request: RequestRow) => {
    switch (request.type) {
      case RequestType.ScheduleChange:
        navigate(`/team-member/${userId}/schedule-update/${request.id}`);
        break;
      case RequestType.LeaveRequest:
        navigate(`/team-member/${userId}/leave-request/${request.id}`);
        break;
      case RequestType.AvailabilityChange:
        navigate(`/team-member/${userId}/availability-exception/${request.id}`);
        break;
    }
  };

  return (
    <section id={requestsSectionId} className="flex flex-col gap-4">
      <Feature name="requests">
        <TSTitleCard title={strings.timeshift.teamMember.menu.requests} icon={FiBell}>
          <Suspense fallback={<TableLoader />}>
            <TeamMemberRequestTable
              requests={requests}
              onViewDetails={(requestId: string) => {
                const request = requests.find(request => request.id === requestId);
                if (request) {
                  handleViewDetails(request);
                }
              }}
            />
          </Suspense>
        </TSTitleCard>
      </Feature>
    </section>
  );
};
